import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Divider, 
  Typography, 
  Space, 
  Avatar, 
  Row, 
  Col,
  message,
  Spin
} from 'antd';
import './index.css';
import Navbar from '../../layouts/navbar';
import Footer from '../../layouts/footer';
import { 
  UserOutlined, 
  MailOutlined, 
  PhoneOutlined, 
  HomeOutlined, 
  EnvironmentOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { mstAuth, mstGeneral } from '../../mobx';
import { useAuth } from '../../layouts/auth/AuthContext';
import { useReferral } from '../../utils/referral';

const { Text, Title } = Typography;
const { TextArea } = Input;

const EditProfileScreen = observer((props) => {
  const {navigateWithReferral} = useReferral();;
  const { user, updateUser } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [saving, setSaving] = useState(false);

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        firstname: user.firstname,
        last_name: user.last_name,
        email: user.email,
        phone: user.phone,
        address: user.address,
        postcode: user.postcode,
        country: user.country,
        state: user.state
      });
    }
  }, [user, form]);

  useEffect(() => {
    // Fetch countries and states
    const fetchData = async () => {
      setLoading(true);
      try {
        const [statesResponse, countriesResponse] = await Promise.all([
          mstGeneral.fetchStates(),
          mstGeneral.fetchCountries()
        ]);

        const statesOptions = statesResponse.map(item => ({
          label: item.state_name,
          value: item.state_name
        }));

        const countriesOptions = countriesResponse.map(item => ({
          label: item.country_name,
          value: item.country_name
        }));

        setStates(statesOptions);
        setCountries(countriesOptions);
      } catch (error) {
        console.error('Failed to load data:', error);
        message.error('Failed to load countries and states');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const onSave = async (values) => {
    setSaving(true);
    try {
      const params = {
        first_name: values.firstname,
        last_name: values.last_name,
        email: values.email,
        state: values.state,
        country: values.country,
        postcode: values.postcode,
        phone_number: values.phone,
        address: values.address
      };

      const response = await mstAuth.updateUserProfile(params);
      
      // Update local user context
      if (updateUser) {
        updateUser({ ...user, ...params });
      }
      
      message.success('Profile updated successfully!');
    } catch (error) {
      console.error('Update failed:', error);
      message.error(error.error_message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name) => {
    return name ? name.charAt(0).toUpperCase() : 'U';
  };

  if (loading) {
    return (
      <div id="EditProfileScreen">
        <div className="container">
          <Navbar />
          <div className="loading-container">
            <Spin size="large" />
            <Text style={{ color: '#6c757d', fontSize: '13px' }}>Loading profile...</Text>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div id="EditProfileScreen">
      <div className="container">
        <Navbar />
        
        {/* Header Section */}
        <div className="profile-header">
          <div className="avatar-section">
            <Avatar 
              size={64} 
              icon={<UserOutlined />}
              className="profile-avatar"
            >
              {getInitials(user?.firstname)}
            </Avatar>
            <div className="profile-info">
              <Title level={3} className="profile-name">
                {user?.firstname} {user?.last_name}
              </Title>
              <Text className="profile-email">
                {user?.email}
              </Text>
              <div className="member-status">
                <span className={`status-badge ${user?.member_status?.toLowerCase()}`}>
                  {user?.member_status}
                </span>
              </div>
            </div>
          </div>
        </div>

        <Divider className="section-divider" />

        {/* Profile Form */}
        <Card 
          className="profile-form-card"
          title="Edit Profile Information"
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={onSave}
            className="profile-form"
          >
            <Row gutter={[16, 0]}>
              {/* Personal Information */}
              <Col xs={24} lg={12}>
                <Title level={4} className="form-section-title">
                  Personal Information
                </Title>
                
                <Form.Item
                  name="firstname"
                  label="First Name"
                  rules={[{ required: true, message: 'First name is required' }]}
                >
                  <Input 
                    prefix={<UserOutlined style={{ color: '#6c757d' }} />}
                    placeholder="Enter your first name"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="last_name"
                  label="Last Name"
                >
                  <Input 
                    prefix={<UserOutlined style={{ color: '#6c757d' }} />}
                    placeholder="Enter your last name"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="email"
                  label="Email Address"
                  rules={[
                    { required: true, message: 'Email is required' },
                    { type: 'email', message: 'Please enter a valid email' }
                  ]}
                >
                  <Input 
                    prefix={<MailOutlined style={{ color: '#6c757d' }} />}
                    placeholder="Enter your email"
                    size="large"
                    disabled
                  />
                </Form.Item>

                <Form.Item
                  name="phone"
                  label="Phone Number"
                  rules={[{ required: true, message: 'Phone number is required' }]}
                >
                  <Input 
                    prefix={<PhoneOutlined style={{ color: '#6c757d' }} />}
                    placeholder="Enter your phone number"
                    size="large"
                  />
                </Form.Item>
              </Col>

              {/* Address Information */}
              <Col xs={24} lg={12}>
                <Title level={4} className="form-section-title">
                  Address Information
                </Title>

                <Form.Item
                  name="address"
                  label="Address"
                >
                  <TextArea 
                    placeholder="Enter your full address"
                    rows={3}
                    size="large"
                  />
                </Form.Item>

                <Row gutter={8}>
                  <Col span={12}>
                    <Form.Item
                      name="postcode"
                      label="Postcode"
                    >
                      <Input 
                        prefix={<HomeOutlined style={{ color: '#6c757d' }} />}
                        placeholder="Postcode"
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="country"
                      label="Country"
                      rules={[{ required: true, message: 'Country is required' }]}
                    >
                      <Input 
                        placeholder="Country"
                        size="large"
                        list="countries"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="state"
                  label="State"
                  rules={[{ required: true, message: 'State is required' }]}
                >
                  <Input 
                    placeholder="Select state"
                    size="large"
                    list="states"
                  />
                </Form.Item>

                <datalist id="countries">
                  {countries.map((country, index) => (
                    <option key={index} value={country.value} />
                  ))}
                </datalist>
                <datalist id="states">
                  {states.map((state, index) => (
                    <option key={index} value={state.value} />
                  ))}
                </datalist>
              </Col>
            </Row>

            {/* Action Buttons */}
            <Divider style={{ margin: '20px 0' }} />
            <div className="form-actions">
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={saving}
                icon={<SaveOutlined />}
                className="save-button"
              >
                Save Changes
              </Button>
              <Button 
                className="cancel-button"
                onClick={() => navigateWithReferral(-1)}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Card>
      <Footer />
      </div>
    </div>
  );
});

export default EditProfileScreen;